import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, Phone, Mail, MapPin, Send, Loader2, Facebook, Linkedin, Link2, MessageCircle, Check } from "lucide-react";
import { BUSINESS } from "../data/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterForm = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/subscribers`, { email });
            toast.success("Inscrição confirmada!", {
                description: "Você receberá as novidades tributárias da WBS.",
            });
            setEmail("");
        } catch {
            toast.error("Não foi possível concluir.", { description: "Verifique o e-mail e tente novamente." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu melhor e-mail"
                data-testid="newsletter-email-input"
                className="flex-1 rounded-lg bg-white/10 border border-white/20 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400 transition-colors duration-200"
            />
            <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="shrink-0 inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm px-6 py-3 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
            >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Inscrever
            </button>
        </form>
    );
};

const ShareButtons = () => {
    const [copied, setCopied] = useState(false);
    const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
    const shareText = "Conheça a WBS Serviços Contábeis — contabilidade para MEI, Simples Nacional, Lucro Presumido e Lucro Real:";

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(siteUrl);
            setCopied(true);
            toast.success("Link copiado!");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Não foi possível copiar.");
        }
    };

    const buttons = [
        {
            testid: "share-whatsapp",
            label: "WhatsApp",
            icon: MessageCircle,
            href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${siteUrl}`)}`,
        },
        {
            testid: "share-facebook",
            label: "Facebook",
            icon: Facebook,
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}`,
        },
        {
            testid: "share-linkedin",
            label: "LinkedIn",
            icon: Linkedin,
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(siteUrl)}`,
        },
    ];

    return (
        <div className="mt-4 flex flex-wrap gap-3" data-testid="share-buttons">
            {buttons.map((btn) => (
                <a
                    key={btn.testid}
                    href={btn.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={btn.testid}
                    aria-label={`Compartilhar no ${btn.label}`}
                    className="flex items-center justify-center w-11 h-11 rounded-lg bg-white/10 border border-white/20 text-slate-300 hover:text-amber-400 hover:border-amber-400/50 transition-all duration-200 hover:-translate-y-0.5"
                >
                    <btn.icon className="w-5 h-5" />
                </a>
            ))}
            <button
                onClick={copyLink}
                data-testid="share-copy-link"
                aria-label="Copiar link do site"
                className="flex items-center justify-center w-11 h-11 rounded-lg bg-white/10 border border-white/20 text-slate-300 hover:text-amber-400 hover:border-amber-400/50 transition-all duration-200 hover:-translate-y-0.5"
            >
                {copied ? <Check className="w-5 h-5 text-amber-400" /> : <Link2 className="w-5 h-5" />}
            </button>
        </div>
    );
};

export const Footer = () => {
    return (
        <footer data-testid="footer" className="bg-slate-900 text-slate-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid lg:grid-cols-2 gap-10 pb-12 mb-12 border-b border-white/10">
                    <div data-testid="newsletter-block">
                        <h3 className="text-lg font-semibold text-white" data-testid="newsletter-title">
                            Receba novidades tributárias por e-mail
                        </h3>
                        <p className="mt-1.5 text-sm text-slate-400">
                            Mudanças fiscais e prazos importantes, direto na sua caixa de entrada.
                        </p>
                        <NewsletterForm />
                        <p className="mt-3 text-xs text-slate-500">
                            Ao se inscrever, você autoriza o contato da WBS por e-mail (LGPD). Cancele quando quiser.
                        </p>
                    </div>
                    <div className="lg:justify-self-end" data-testid="share-block">
                        <h3 className="text-lg font-semibold text-white" data-testid="share-title">
                            Compartilhe nosso site
                        </h3>
                        <p className="mt-1.5 text-sm text-slate-400">
                            Indique a WBS para um empreendedor que você conhece.
                        </p>
                        <ShareButtons />
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-12">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 text-amber-400">
                                <Calculator className="w-5 h-5" />
                            </span>
                            <span className="leading-tight">
                                <span className="block text-lg font-bold tracking-tight text-white">WBS</span>
                                <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Serviços Contábeis</span>
                            </span>
                        </div>
                        <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-xs" data-testid="footer-tagline">
                            Contabilidade próxima e transparente para empresas MEI, Simples Nacional, Lucro Presumido e Lucro Real.
                        </p>
                    </div>

                    <div data-testid="footer-contact">
                        <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white">Contato</h3>
                        <ul className="mt-5 space-y-3 text-sm">
                            <li>
                                <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="footer-phone-link" className="flex items-center gap-2.5 hover:text-amber-400 transition-colors duration-200">
                                    <Phone className="w-4 h-4 text-amber-500" />
                                    {BUSINESS.phoneDisplay}
                                </a>
                            </li>
                            <li>
                                <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email-link" className="flex items-center gap-2.5 hover:text-amber-400 transition-colors duration-200">
                                    <Mail className="w-4 h-4 text-amber-500" />
                                    {BUSINESS.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-2.5" data-testid="footer-address">
                                <MapPin className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                                <span>{BUSINESS.address}</span>
                            </li>
                        </ul>
                    </div>

                    <div data-testid="footer-legal">
                        <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white">Dados do escritório</h3>
                        <ul className="mt-5 space-y-3 text-sm text-slate-400">
                            <li data-testid="footer-cnpj">CNPJ: {BUSINESS.cnpj}</li>
                            <li data-testid="footer-crc">Registro: {BUSINESS.crc}</li>
                            <li>Atendimento online para todo o Brasil</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
                    <p data-testid="footer-copyright">© {new Date().getFullYear()} {BUSINESS.name}. Todos os direitos reservados.</p>
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        data-testid="footer-back-to-top"
                        className="hover:text-amber-400 transition-colors duration-200 font-semibold"
                    >
                        Voltar ao topo
                    </button>
                </div>
            </div>
        </footer>
    );
};
